import * as React from 'react';
import { Separator } from '@components/atoms';
import { Card, CardContent } from '@components/molecules';
import { cn } from 'src';

interface OverviewShortDetailsWrapperProps {
	externalLinkButton?: React.ReactNode;
	closeButton?: React.ReactNode;
	cardClassName?: string;
}

type OverviewShortDetailsVariant =
	| 'organizationOverview'
	| 'customerOverview'
	| 'mergeCustomer'
	| 'reservationOverview';

interface OverviewShortDetailsCardProps extends OverviewShortDetailsWrapperProps {
	avatar: React.ReactNode;
	topComponent: React.ReactNode;
	bottomComponent: React.ReactNode;
	editButton?: React.ReactNode;
	actionButton?: React.ReactNode;
	variant?: OverviewShortDetailsVariant;
}

const SEPERATOR_BELOW_TOP_COMPONENT_VARIANTS = ['organizationOverview', 'reservationOverview'];

const OverviewShortDetailsWrapper: React.FC<React.PropsWithChildren<OverviewShortDetailsWrapperProps>> = ({
	children,
	externalLinkButton,
	closeButton,
	cardClassName,
}) => (
	<span data-aid="overview-short-details">
		<Card className={cn('relative h-full p-4 text-primary', cardClassName)}>
			{closeButton && (
				<div className="absolute top-3 right-3" data-aid="overview-short-details-close">
					{closeButton}
				</div>
			)}
			{children}
			{externalLinkButton && (
				<div className="absolute right-3 bottom-3" data-aid="overview-short-details-external-link">
					{externalLinkButton}
				</div>
			)}
		</Card>
	</span>
);

const OverviewShortDetailsLG = React.forwardRef<HTMLDivElement, OverviewShortDetailsCardProps>(
	(
		{ avatar, topComponent, bottomComponent, editButton, externalLinkButton, closeButton, cardClassName, actionButton },
		ref
	) => (
		<OverviewShortDetailsWrapper
			externalLinkButton={externalLinkButton}
			closeButton={closeButton}
			cardClassName={cardClassName}
		>
			<CardContent ref={ref} className={`flex items-center gap-6 ${closeButton || externalLinkButton ? 'mr-8' : ''}`}>
				<div>{avatar}</div>
				<div className="flex min-w-0 flex-1 flex-col gap-2">
					<div className="flex">{topComponent}</div>
					<Separator className="bg-primary" />
					<div className="flex min-w-0 justify-between">{bottomComponent}</div>
				</div>
				{editButton && <div data-aid="overview-short-details-action">{editButton}</div>}
				{actionButton && <div data-aid="overview-short-details-action-container">{actionButton}</div>}
			</CardContent>
		</OverviewShortDetailsWrapper>
	)
);
OverviewShortDetailsLG.displayName = 'OverviewShortDetailsLG';

const OverviewShortDetailsMD = React.forwardRef<HTMLDivElement, OverviewShortDetailsCardProps>(
	(
		{
			avatar,
			topComponent,
			bottomComponent,
			editButton,
			externalLinkButton,
			closeButton,
			variant = 'organizationOverview',
			cardClassName,
		},
		ref
	) => (
		<OverviewShortDetailsWrapper
			closeButton={closeButton}
			externalLinkButton={externalLinkButton}
			cardClassName={cardClassName}
		>
			<CardContent ref={ref} className="flex flex-col gap-5">
				<div className="relative flex items-center justify-center">
					<div>{avatar}</div>
					<div className="absolute right-0">{editButton}</div>
				</div>
				{variant === 'mergeCustomer' && topComponent}
				<Separator className="bg-primary" />
				<div>
					<div className="flex flex-col gap-7">
						{SEPERATOR_BELOW_TOP_COMPONENT_VARIANTS.includes(variant) && topComponent}
						{bottomComponent}
					</div>
				</div>
			</CardContent>
		</OverviewShortDetailsWrapper>
	)
);
OverviewShortDetailsMD.displayName = 'OverviewShortDetailsMD';

const OverviewShortDetails: React.FC<OverviewShortDetailsCardProps> = props => {
	return (
		<>
			<div className="h-[140px] xs:hidden lg:block">
				<OverviewShortDetailsLG {...props} />
			</div>
			<div className="lg:hidden">
				<OverviewShortDetailsMD {...props} />
			</div>
		</>
	);
};

export { OverviewShortDetails };
